import React, { useEffect, useRef, createElement } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { ContactSection } from '../components/ContactSection';
import { motion } from 'framer-motion';
import { Wind, TrendingUp, MapPin, Waves, Sailboat, CheckCircle2, GraduationCap, Users, ShieldCheck, Package, Award, Coffee } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { captureUtmParams } from '../utils/utmTracking';
import { useNavigate } from 'react-router-dom';
const spotFeatures = [{
  icon: Wind,
  title: 'רוח צד (Side Shore)',
  description: 'תנאי רוח אידיאליים לגלישה בטוחה - הרוח נושבת לאורך החוף'
}, {
  icon: MapPin,
  title: 'מיקום מושלם בכנרת',
  description: 'חוף מעגן עדן - אחד החופים הטובים ביותר לספורט רוח'
}, {
  icon: Waves,
  title: 'מים שטוחים',
  description: 'מושלם ללמידה והתקדמות מהירה'
}];
const courses = [{
  title: 'קורס ווינג פויל למתחילים',
  description: 'לימוד מאפס, כולל תיאוריה ופרקטיקה על המים',
  icon: GraduationCap
}, {
  title: 'קורס מתקדמים',
  description: 'שיפור טכניקה, מהפכים ועוד',
  icon: TrendingUp
}, {
  title: 'הדרכות סאפ',
  description: 'שיעורי סאפ לכל הרמות - ממתחילים ועד מתקדמים',
  icon: Waves
}, {
  title: 'הדרכות קיאק וסרפסקי',
  description: 'לימוד חתירה מקצועית בקיאק ובסרפסקי',
  icon: Sailboat
}, {
  title: 'קבוצות סאפ',
  description: 'אימוני סאפ קבוצתיים באווירה כיפית ותומכת',
  icon: Users
}, {
  title: 'הדרכות אישיות',
  description: 'שיעור פרטי מותאם אישית לרמה שלך',
  icon: Users
}, {
  title: 'גלישת רוח (Windsurfing)',
  description: 'הדרכות לכל הרמות, ממתחילים ועד מתקדמים',
  icon: Sailboat
}];
const clubFeatures = [{
  title: 'שימוש חופשי בציוד חדיש ואיכותי',
  icon: ShieldCheck
}, {
  title: 'אחסון ציוד אישי במועדון',
  icon: Package
}, {
  title: 'מתקן לטליית ווינגים וקומפרסור',
  icon: Wind
}, {
  title: 'מדשאות נוחות להכנת ציוד',
  icon: MapPin
}, {
  title: 'מוזיקה ופינת קפה',
  icon: Coffee
}, {
  title: 'חניה קרובה למים',
  icon: CheckCircle2
}, {
  title: 'קהילת גולשים פעילה ותומכת',
  icon: Award
}];
function WindMeterWidget() {
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // In RTL, scroll starts at the right. Scroll to left (0) to show the wind circle first.
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = 0;
    }
  }, []);
  return <div ref={scrollRef} dir="ltr" className="bg-white rounded-xl md:rounded-3xl shadow-xl border border-gray-200 max-w-4xl mx-auto relative overflow-x-auto" data-id="element-2431" data-mp="3vu8pex29745d">
      <div className="absolute inset-0 z-10" data-id="element-2432" data-mp="338p8pzyvtcj0" />
      <iframe src="https://widget.holfuy.com/?station=2020&su=knots&te=C&lang=he&bg=ffffff" className="h-[320px] border-0" style={{
      minWidth: '520px',
      width: '100%'
    }} title="Wind Station - Beach Club Maagan" scrolling="no" data-id="element-2433" data-mp="3hwabfsruh9qe" />
    </div>;
}
export function WindMeterPage() {
  const navigate = useNavigate();
  useEffect(() => {
    captureUtmParams();
    document.title = 'מד רוח כנרת';
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'מד רוח כנרת בזמן אמת בחוף מעגן עדן. בדקו את תנאי הרוח לווינג פויל, גלישת רוח, סאפ וקיאק');
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', 'מד רוח כנרת, רוח כנרת, תנאי רוח כנרת, ווינג פויל כנרת, גלישת רוח כנרת, סאפ כנרת, חוף מעגן עדן');
    const ogTags = [{
      property: 'og:title',
      content: 'מד רוח כנרת'
    }, {
      property: 'og:description',
      content: 'מד רוח כנרת בזמן אמת בחוף מעגן עדן - מושלם לווינג פויל, גלישת רוח, סאפ וקיאק'
    }, {
      property: 'og:type',
      content: 'website'
    }, {
      property: 'og:url',
      content: window.location.href
    }];
    ogTags.forEach(tag => {
      let metaTag = document.querySelector(`meta[property="${tag.property}"]`);
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('property', tag.property);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', tag.content);
    });
  }, []);
  return <div className="min-h-screen bg-white" dir="rtl" data-id="element-2434" data-mp="whrgh5ntm0we">
      <Navigation data-id="element-2435" data-mp="wfwsfih95so3" />

      <div className="relative h-[50vh] min-h-[400px] overflow-hidden bg-gradient-to-br from-[#0A5F8F] to-[#0D7AB8]" data-id="element-2436" data-mp="sgecl14ag7s4">
        <div className="absolute inset-0 opacity-10" data-id="element-2437" data-mp="nhupuyhveryp">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent_50%)]" data-id="element-2438" data-mp="njfg4bx5joqr" />
        </div>
        <div className="relative z-10 container mx-auto px-4 h-full flex flex-col items-center justify-center text-center text-white" data-id="element-2439" data-mp="3870ukgz60tnx">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-2440" data-mp="1nr2q8so4sxv2">
            <div className="inline-flex items-center gap-3 bg-white/20 backdrop-blur-md px-6 py-3 rounded-full mb-6 border border-white/30" data-id="element-2441" data-mp="1wpedqltpbfja">
              <Wind className="w-6 h-6" data-id="element-2442" data-mp="21ir3iza49sdm" />
              <span className="font-medium text-lg" data-id="element-2443" data-mp="26gsm7j3mrpok">תנאי רוח בזמן אמת</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6" data-id="element-2444" data-mp="2beawbctdway5">
              מד רוח חוף מעגן - כנרת
            </h1>
            <p className="text-xl md:text-2xl font-light max-w-2xl mx-auto text-blue-100" data-id="element-2445" data-mp="2gfi3parm784a">
              בדקו את תנאי הרוח לפני שאתם יוצאים למים
            </p>
          </motion.div>
        </div>
      </div>

      <section className="py-16 bg-gray-50" data-id="element-2446" data-mp="m1qjxifkn7v4">
        <div className="container mx-auto px-4" data-id="element-2447" data-mp="q1r6xrgqkrds">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-6xl mx-auto" data-id="element-2448" data-mp="q29fnnkl4y2l">
            <div className="text-center mb-8" data-id="element-2449" data-mp="1ofoh7u4k4gpa">
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A5F8F] mb-4" data-id="element-2450" data-mp="22dlfqw2kmx6l">
                מד רוח בזמן אמת
              </h2>
              <p className="text-gray-600 text-lg" data-id="element-2451" data-mp="1ya6o1859ssdb">
                נתוני רוח, טמפרטורה ותנאי מזג אוויר מעודכנים כל דקה
              </p>
            </div>
            <div className="max-w-2xl mx-auto mb-6 space-y-3" data-id="element-2452" data-mp="27cy1fdhphy7l">
              <div className="bg-blue-50 border border-blue-200 rounded-xl px-5 py-3 flex items-start gap-3 text-right" data-id="element-2453" data-mp="27abthcqwlgou">
                <Wind className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" data-id="element-2454" data-mp="2h29aj04lf2vh" />
                <p className="text-blue-800 text-sm font-medium" data-id="element-2455" data-mp="2h4c5lyklrm72">
                  ברוחות מערביות, בתוך המים יש כ-3-4 קשרים יותר ממה שמראה מד
                  הרוח.
                </p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 flex items-start gap-3 text-right" data-id="element-2456" data-mp="2q12yy37rsmxy">
                <span className="text-amber-500 shrink-0 mt-0.5" data-id="element-2457" data-mp="3enk5o1bleeye">📷</span>
                <p className="text-amber-800 text-sm font-medium" data-id="element-2458" data-mp="3elg59tcwjy2v">
                  בקרוב — מצלמת חוף בזמן אמת!
                </p>
              </div>
            </div>
            <WindMeterWidget data-id="element-2459" data-mp="34o1o1rj5n59i" />
            <div dir="ltr" className="bg-white rounded-xl md:rounded-3xl shadow-xl border border-gray-200 max-w-4xl mx-auto relative overflow-x-auto mt-6" data-id="element-2460" data-mp="34rh5h10awqe3">
              <div className="absolute inset-0 z-10" data-id="element-2461" data-mp="2vpq1q1pbmtmv" />
              <iframe src="https://widget.holfuy.com/?mode=graphs&station=2020&su=knots" className="h-[580px] md:h-[850px] border-0" style={{
              minWidth: '520px',
              width: '100%'
            }} title="Wind Graphs - Beach Club Maagan" scrolling="no" data-id="element-2462" data-mp="2h021z4wsg319" />
            </div>
            <div dir="ltr" className="bg-white rounded-xl md:rounded-3xl shadow-xl border border-gray-200 max-w-4xl mx-auto relative overflow-x-auto mt-6" data-id="element-2463" data-mp="2cygr1p3tdytp">
              <div className="absolute inset-0 z-10" data-id="element-2464" data-mp="2sf56cimurikg" />
              <iframe src="https://api.holfuy.com/archive/?s=2020" className="h-[600px] md:h-[700px] border-0" style={{
              minWidth: '520px',
              width: '100%'
            }} title="Wind Archive - Beach Club Maagan" scrolling="no" data-id="element-2465" data-mp="2nhmzcxsebdf3" />
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-gray-50" data-id="element-2466" data-mp="29hvmx1hj2537">
        <div className="container mx-auto px-4" data-id="element-2467" data-mp="29joz0wawthil">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-2468" data-mp="24qlz86fjhbty">
            <div className="inline-flex items-center gap-2 bg-cyan-100 text-cyan-600 px-4 py-2 rounded-full font-bold mb-4" data-id="element-2469" data-mp="1uu0sngnvfso0">
              <GraduationCap className="w-5 h-5" data-id="element-2470" data-mp="1qt75wqm60jwp" />
              <span data-id="element-2471" data-mp="1lrrmf2zmylgf">ללמוד ולגלוש</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A5F8F] mb-4" data-id="element-2472" data-mp="1ludsszasiux2">
              קורסים והדרכות לכל הרמות
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-2473" data-mp="68aib792suh0">
              ממתחילים שעושים צעדים ראשונים ועד גולשים מתקדמים שרוצים לשפר
              טכניקה - המים השטוחים של הכנרת הופכים את הלמידה לקלה ובטוחה.
            </p>
          </motion.div>

          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-2474" data-mp="f7nqlwlgrd05">
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-2475" data-mp="3eqq41f2ts9d4">
              <h3 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-2476" data-mp="3nnh03rhdno7p">
                ההדרכות שלנו
              </h3>
              <div className="space-y-6" data-id="element-2477" data-mp="2v0w8qr0wjc45">
                {courses.map((course, index) => <div key={index} className="flex gap-4 bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow" data-id="element-2478" data-mp="2v0mir3fux4yg" data-mpi={index}>
                    <div className="bg-cyan-50 p-3 rounded-xl h-fit shrink-0" data-id="element-2479" data-mp="2b0wm0mg3quzp">
                      <course.icon className="w-6 h-6 text-cyan-600" data-id="element-2480" data-mp="2f1gh7kh47oj7" />
                    </div>
                    <div data-id="element-2481" data-mp="2k12utv0uv6sj">
                      <h4 className="text-xl font-bold text-gray-900 mb-2" data-id="element-2482" data-mp="2k0jl31lczopl">
                        {course.title}
                      </h4>
                      <p className="text-gray-600" data-id="element-2483" data-mp="2oxtltde77msq">{course.description}</p>
                    </div>
                  </div>)}
              </div>
              <a href="https://wa.me/972522632784" target="_blank" rel="noopener noreferrer" className="mt-8 w-full bg-[#25D366] hover:bg-[#20bd5a] text-white text-lg font-bold py-4 px-6 rounded-xl transition-colors flex items-center justify-center gap-3 shadow-md" data-id="element-2484" data-mp="1wape30nq0ud9">
                <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor" data-id="element-2485" data-mp="262v444uex1pt">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" data-id="element-2486" data-mp="18jjxgj9yhy1w" />
                </svg>
                לתיאום שיעור
              </a>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-2487" data-mp="3pvwxbfz90oh3">
              <div className="bg-[#0A5F8F] rounded-3xl p-8 md:p-10 text-white h-full relative overflow-hidden" data-id="element-2488" data-mp="3guypsnplpytr">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-2489" data-mp="8gq31w9rg29i" />
                <div className="relative z-10" data-id="element-2490" data-mp="cgh02hpvremh">
                  <h3 className="text-2xl font-bold mb-8" data-id="element-2491" data-mp="3uvro7jj0xe25">מה כולל המועדון?</h3>
                  <ul className="space-y-5" data-id="element-2492" data-mp="3oyko90rd2ug">
                    {clubFeatures.map((feature, index) => <li key={index} className="flex items-center gap-4" data-id="element-2493" data-mp="3mlux9ordo6u" data-mpi={index}>
                        <div className="bg-white/10 p-2 rounded-lg shrink-0" data-id="element-2494" data-mp="rcyejhtd99d9">
                          <feature.icon className="w-5 h-5 text-cyan-300" data-id="element-2495" data-mp="w6awxa7gcjxk" />
                        </div>
                        <span className="text-lg text-blue-50" data-id="element-2496" data-mp="15iqj8ehwb7o7">
                          {feature.title}
                        </span>
                      </li>)}
                  </ul>
                  <div className="mt-10 pt-8 border-t border-white/20" data-id="element-2497" data-mp="1fa4gsgqadxep">
                    <button className="w-full bg-white text-[#0A5F8F] hover:bg-blue-50 text-lg py-4 px-6 rounded-xl font-bold transition-colors" onClick={() => document.getElementById('contact')?.scrollIntoView({
                    behavior: 'smooth'
                  })} data-id="element-2498" data-mp="1ab1jjigjsvha">
                      לתיאום שיעור
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-blue-50 to-cyan-50" data-id="element-2499" data-mp="rmd3b9t0febu">
        <div className="container mx-auto px-4" data-id="element-2500" data-mp="27wyumusllyj7">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-2501" data-mp="22zp8pp4f4504">
            <div className="inline-flex items-center gap-2 bg-cyan-100 text-cyan-600 px-4 py-2 rounded-full font-bold mb-4" data-id="element-2502" data-mp="hgmwpk7q17hr">
              <Sailboat className="w-5 h-5" data-id="element-2503" data-mp="mey5drma5bye" />
              <span data-id="element-2504" data-mp="qi4gpdo91tbq">המקום המושלם</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A5F8F] mb-4" data-id="element-2505" data-mp="qjo0he946mi9">
              למה חוף מעגן מושלם לספורט רוח?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-2506" data-mp="10gahfrybktf0">
              תנאים אידיאליים לווינג פויל וגלישת רוח לכל הרמות
            </p>
          </motion.div>

          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2507" data-mp="1593popszyjvu">
            {spotFeatures.map((feature, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-2xl p-8 shadow-lg border border-cyan-100" data-id="element-2508" data-mp="1e7p5weazxmtg" data-mpi={index}>
                <div className="bg-cyan-100 w-14 h-14 rounded-2xl flex items-center justify-center mb-4" data-id="element-2509" data-mp="2yoyd807nimcd">
                  <feature.icon className="w-7 h-7 text-cyan-600" data-id="element-2510" data-mp="2uoo4ifsh5htt" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-2511" data-mp="2pqcuo526j7bl">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-lg leading-relaxed" data-id="element-2512" data-mp="3mhesy43x228i">
                  {feature.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-[#0A5F8F] to-[#0D7AB8] text-white" data-id="element-2513" data-mp="3dkw350qzeug5">
        <div className="container mx-auto px-4" data-id="element-2514" data-mp="38nnjevq6qics">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-4xl mx-auto text-center" data-id="element-2515" data-mp="9yfbmx58rrsd">
            <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-2516" data-mp="o3z3g1xneks4">
              מוכנים לעוף מעל המים?
            </h2>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed" data-id="element-2517" data-mp="t1qxp2lrmizh">
              הצטרפו לקהילת הווינג פויל והגלישה שלנו
              <br data-id="element-2518" data-mp="jch39g3g8o87" />
              נלמד אתכם לנצל כל רוח!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center" data-id="element-2519" data-mp="jaco2203o2sm">
              <Button className="bg-white text-[#0A5F8F] hover:bg-blue-50 text-lg py-6 px-8" onClick={() => navigate('/wingfoil')} data-id="element-2520" data-mp="5f0tpvpuwec8">
                למידע על Wing Foil
              </Button>
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 text-lg py-6 px-8" onClick={() => document.getElementById('contact')?.scrollIntoView({
              behavior: 'smooth'
            })} data-id="element-2521" data-mp="3sjgpkc9tehki">
                צור קשר
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <ContactSection data-id="element-2522" data-mp="3f9esvg2exyll" />
      <Footer data-id="element-2523" data-mp="3fapyzmjbzifv" />
    </div>;
}